import { useState } from "react";
import { PricingConfig, DailyRewardDay } from "../../types/config";
import { Section, NumberInput, Toggle, IconSelector } from "./SharedComponents";

interface SectionProps {
    config: PricingConfig;
    isNew: boolean;
    saving: boolean;
    saved: boolean;
    hasChanges: boolean;
    onSave: (data: Partial<PricingConfig>) => void;
}

export const PricesSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateConfig }: SectionProps & { updateConfig: any }) => (
    <Section
        title="Subscription Prices"
        icon="💎"
        onSave={() => onSave({
            pro_monthly_stars: config.pro_monthly_stars,
            pro_yearly_stars: config.pro_yearly_stars,
        })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <div className="grid grid-cols-2 gap-6">
            <NumberInput
                label="Pro Monthly (Stars)"
                value={config.pro_monthly_stars}
                onChange={(v) => updateConfig("pro_monthly_stars", v)}
            />
            <NumberInput
                label="Pro Yearly (Stars)"
                value={config.pro_yearly_stars}
                onChange={(v) => updateConfig("pro_yearly_stars", v)}
                description="~17% discount (10 months for 12)"
            />
        </div>
    </Section>
);

export const ReferralSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateReferral }: SectionProps & { updateReferral: any }) => (
    <Section
        title="Referral Rewards"
        icon="🎁"
        onSave={() => onSave({ referral_rewards: config.referral_rewards })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <div className="mb-4">
            <Toggle
                label="Enable Referral Program"
                checked={config.referral_rewards.enabled}
                onChange={(v) => updateReferral("enabled", v)}
            />
        </div>
        <div className="grid grid-cols-2 gap-6">
            <NumberInput
                label="Referrer Text Credits"
                value={config.referral_rewards.referrer_text_credits}
                onChange={(v) => updateReferral("referrer_text_credits", v)}
                description="Given to user who invites"
            />
            <NumberInput
                label="Referrer Voice Credits"
                value={config.referral_rewards.referrer_voice_credits}
                onChange={(v) => updateReferral("referrer_voice_credits", v)}
            />
            <NumberInput
                label="Referred Text Credits"
                value={config.referral_rewards.referred_text_credits}
                onChange={(v) => updateReferral("referred_text_credits", v)}
                description="Given to invited user"
            />
            <NumberInput
                label="Referred Voice Credits"
                value={config.referral_rewards.referred_voice_credits}
                onChange={(v) => updateReferral("referred_voice_credits", v)}
            />
        </div>
    </Section>
);

export const TierLimitsSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateTier }: SectionProps & { updateTier: any }) => (
    <Section
        title="Tier Limits"
        icon="📊"
        onSave={() => onSave({ tier_limits: config.tier_limits })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <p className="text-slate-500 text-sm mb-4">Daily message limits per tier. Leave empty for unlimited.</p>
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
                <div className="text-slate-400 text-sm font-medium">Tier</div>
                <div className="text-slate-400 text-sm font-medium">Text/Day</div>
                <div className="text-slate-400 text-sm font-medium">Voice/Day</div>
            </div>
            {(["free", "pro"] as const).map((tier) => (
                <div key={tier} className="grid grid-cols-3 gap-4 items-center">
                    <div className="text-white font-medium capitalize">{tier}</div>
                    <input
                        type="number"
                        value={config.tier_limits[tier].text_per_day ?? ""}
                        onChange={(e) =>
                            updateTier(tier, "text_per_day", e.target.value ? parseInt(e.target.value) : undefined)
                        }
                        placeholder="Unlimited"
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    />
                    <input
                        type="number"
                        value={config.tier_limits[tier].voice_per_day ?? ""}
                        onChange={(e) =>
                            updateTier(tier, "voice_per_day", e.target.value ? parseInt(e.target.value) : undefined)
                        }
                        placeholder="Unlimited"
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    />
                </div>
            ))}
        </div>
    </Section>
);

export const BoosterPacksSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateBooster, addBooster, removeBooster }: SectionProps & { updateBooster: any, addBooster: any, removeBooster: any }) => {
    const [newPackId, setNewPackId] = useState("");

    const handleAdd = () => {
        if (newPackId && !config.booster_packs[newPackId]) {
            addBooster(newPackId);
            setNewPackId("");
        }
    };

    return (
        <Section
            title="Booster Packs"
            icon="🚀"
            onSave={() => onSave({ booster_packs: config.booster_packs })}
            saving={saving}
            saved={saved}
            hasChanges={hasChanges || isNew}
        >
            <div className="space-y-6">
                {Object.entries(config.booster_packs).map(([id, pack]) => (
                    <div key={id} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 relative group">
                        <button
                            onClick={() => removeBooster(id)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs z-10"
                            title="Remove pack"
                        >
                            ✕
                        </button>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex-1 mr-4">
                                <input
                                    type="text"
                                    value={pack.title || ""}
                                    onChange={(e) => updateBooster(id, "title", e.target.value)}
                                    placeholder={id.replace("_", " ").toUpperCase()}
                                    className="bg-transparent border-none text-white font-bold p-0 focus:ring-0 w-full placeholder:text-slate-600"
                                />
                                <input
                                    type="text"
                                    value={pack.description || ""}
                                    onChange={(e) => updateBooster(id, "description", e.target.value)}
                                    placeholder="Optional description"
                                    className="bg-transparent border-none text-slate-400 text-xs p-0 focus:ring-0 w-full placeholder:text-slate-600"
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <IconSelector
                                    value={pack.icon_type || "mic"}
                                    onChange={(v) => updateBooster(id, "icon_type", v)}
                                />
                                <Toggle
                                    label="Enabled"
                                    checked={pack.enabled}
                                    onChange={(v) => updateBooster(id, "enabled", v)}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <NumberInput
                                label="Price (Stars)"
                                value={pack.stars_price}
                                onChange={(v) => updateBooster(id, "stars_price", v)}
                                compact
                            />
                            <div className="relative">
                                <NumberInput
                                    label="Text Credits"
                                    value={pack.text_credits || 0}
                                    onChange={(v) => updateBooster(id, "text_credits", v)}
                                    compact
                                />
                                {pack.text_credits === undefined && (
                                    <button
                                        onClick={() => updateBooster(id, "text_credits", 0)}
                                        className="absolute top-0 right-0 text-[10px] text-blue-400 hover:text-blue-300"
                                    >
                                        + Add Text
                                    </button>
                                )}
                            </div>
                            <NumberInput
                                label="Voice Credits"
                                value={pack.voice_credits}
                                onChange={(v) => updateBooster(id, "voice_credits", v)}
                                compact
                            />
                            <div className="relative">
                                <NumberInput
                                    label="Duration (Days)"
                                    value={pack.duration_days || 0}
                                    onChange={(v) => updateBooster(id, "duration_days", v)}
                                    compact
                                />
                                {pack.duration_days === undefined && (
                                    <button
                                        onClick={() => updateBooster(id, "duration_days", 0)}
                                        className="absolute top-0 right-0 text-[10px] text-blue-400 hover:text-blue-300"
                                    >
                                        + Add Days
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                <div className="flex gap-2 pt-2">
                    <input
                        type="text"
                        value={newPackId}
                        onChange={(e) => setNewPackId(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                        placeholder="new_pack_id"
                        className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <button
                        onClick={handleAdd}
                        disabled={!newPackId || !!config.booster_packs[newPackId]}
                        className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                    >
                        Add Pack
                    </button>
                </div>
            </div>
        </Section>
    );
};

export const ActiveSaleSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateSale }: SectionProps & { updateSale: any }) => (
    <Section
        title="Active Sale"
        icon="🏷️"
        onSave={() => onSave({ active_sale: config.active_sale })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <div className="space-y-4">
            <Toggle
                label="Enable Sale"
                checked={config.active_sale.enabled}
                onChange={(v) => updateSale("enabled", v)}
            />
            <div className="grid grid-cols-2 gap-6">
                <NumberInput
                    label="Discount Percent"
                    value={config.active_sale.discount_percent}
                    onChange={(v) => updateSale("discount_percent", v)}
                    max={100}
                />
                <div>
                    <label className="block text-slate-400 text-sm mb-2">Banner Text</label>
                    <input
                        type="text"
                        value={config.active_sale.banner_text || ""}
                        onChange={(e) => updateSale("banner_text", e.target.value)}
                        placeholder="Optional banner message"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                    />
                </div>
            </div>
            <div>
                <label className="block text-slate-400 text-sm mb-2">Applies To</label>
                <div className="flex flex-wrap gap-2">
                    {["pro_monthly", "pro_yearly", ...Object.keys(config.booster_packs)].map((item) => (
                        <label
                            key={item}
                            className={`px-3 py-1.5 rounded-lg cursor-pointer transition-all ${config.active_sale.applies_to.includes(item)
                                ? "bg-blue-600 text-white"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                                }`}
                        >
                            <input
                                type="checkbox"
                                checked={config.active_sale.applies_to.includes(item)}
                                onChange={(e) => {
                                    const newList = e.target.checked
                                        ? [...config.active_sale.applies_to, item]
                                        : config.active_sale.applies_to.filter((i) => i !== item);
                                    updateSale("applies_to", newList);
                                }}
                                className="sr-only"
                            />
                            {item.replace("_", " ")}
                        </label>
                    ))}
                </div>
            </div>
        </div>
    </Section>
);

export const AdsgramSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateAdsgram }: SectionProps & { updateAdsgram: any }) => (
    <Section
        title="Adsgram Settings"
        icon="📺"
        onSave={() => onSave({ adsgram: config.adsgram })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <div className="mb-4">
            <Toggle
                label="Enable Ad Rewards"
                checked={config.adsgram.enabled}
                onChange={(v) => updateAdsgram("enabled", v)}
            />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
                <label className="block text-slate-400 text-sm mb-2">Block ID</label>
                <input
                    type="text"
                    value={config.adsgram.block_id}
                    onChange={(e) => updateAdsgram("block_id", e.target.value)}
                    placeholder="int-1234"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
                />
            </div>
            <NumberInput
                label="Reward Text Credits"
                value={config.adsgram.reward_text_credits}
                onChange={(v) => updateAdsgram("reward_text_credits", v)}
                description="Credits per ad watched"
            />
            <NumberInput
                label="Max Ads Per Day"
                value={config.adsgram.max_ads_per_day}
                onChange={(v) => updateAdsgram("max_ads_per_day", v)}
            />
        </div>
    </Section>
);

export const SystemSettingsSection = ({ config, isNew, saving, saved, hasChanges, onSave, updateConfig }: SectionProps & { updateConfig: any }) => (
    <Section
        title="System Settings"
        icon="⚙️"
        onSave={() => onSave({
            promo_codes_enabled: config.promo_codes_enabled,
            gemini_cost_per_1m_tokens: config.gemini_cost_per_1m_tokens,
            certificates_enabled: config.certificates_enabled,
            leaderboard_enabled: config.leaderboard_enabled,
            coupons_enabled: config.coupons_enabled,
        })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <div className="grid grid-cols-2 gap-6">
            <Toggle
                label="Promo Codes Enabled"
                checked={config.promo_codes_enabled}
                onChange={(v) => updateConfig("promo_codes_enabled", v)}
            />
            <NumberInput
                label="Gemini Cost (per 1M tokens)"
                value={config.gemini_cost_per_1m_tokens}
                onChange={(v) => updateConfig("gemini_cost_per_1m_tokens", v)}
                step={0.001}
                description="Used for dashboard cost estimations"
            />
        </div>
        <div className="mt-6">
            <h4 className="text-sm font-medium text-slate-400 mb-3">Feature Flags</h4>
            <div className="grid grid-cols-2 gap-6">
                <Toggle
                    label="Certificates"
                    checked={config.certificates_enabled}
                    onChange={(v) => updateConfig("certificates_enabled", v)}
                />
                <Toggle
                    label="Leaderboard"
                    checked={config.leaderboard_enabled}
                    onChange={(v) => updateConfig("leaderboard_enabled", v)}
                />
                <Toggle
                    label="Coupons"
                    checked={config.coupons_enabled}
                    onChange={(v) => updateConfig("coupons_enabled", v)}
                />
            </div>
        </div>
    </Section>
);

export const GamificationSection = ({
    config,
    isNew,
    saving,
    saved,
    hasChanges,
    onSave,
    updateXPRewards,
    updateDailyLoginDay,
    addDailyLoginDay,
    removeDailyLoginDay,
}: SectionProps & {
    updateXPRewards: any;
    updateDailyLoginDay: (dayIndex: number, field: keyof DailyRewardDay, value: number) => void;
    addDailyLoginDay: () => void;
    removeDailyLoginDay: (dayIndex: number) => void;
}) => (
    <Section
        title="Gamification"
        icon="🎮"
        onSave={() => onSave({
            xp_rewards: config.xp_rewards,
            daily_login_rewards: config.daily_login_rewards,
        })}
        saving={saving}
        saved={saved}
        hasChanges={hasChanges || isNew}
    >
        <h4 className="text-sm font-medium text-slate-400 mb-3">XP Rewards</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <NumberInput
                label="Roleplay Complete"
                value={config.xp_rewards.roleplay_complete}
                onChange={(v) => updateXPRewards("roleplay_complete", v)}
                compact
            />
            <NumberInput
                label="Study Complete"
                value={config.xp_rewards.study_complete}
                onChange={(v) => updateXPRewards("study_complete", v)}
                compact
            />
            <NumberInput
                label="Assessment Complete"
                value={config.xp_rewards.assessment_complete}
                onChange={(v) => updateXPRewards("assessment_complete", v)}
                compact
            />
            <NumberInput
                label="Daily Login"
                value={config.xp_rewards.daily_login}
                onChange={(v) => updateXPRewards("daily_login", v)}
                compact
            />
            <NumberInput
                label="Referral Complete"
                value={config.xp_rewards.referral_complete}
                onChange={(v) => updateXPRewards("referral_complete", v)}
                compact
            />
            <NumberInput
                label="Share"
                value={config.xp_rewards.share}
                onChange={(v) => updateXPRewards("share", v)}
                compact
            />
            <NumberInput
                label="Streak Bonus / Day"
                value={config.xp_rewards.streak_bonus_per_day}
                onChange={(v) => updateXPRewards("streak_bonus_per_day", v)}
                compact
            />
            <NumberInput
                label="Streak Bonus Cap"
                value={config.xp_rewards.streak_bonus_cap}
                onChange={(v) => updateXPRewards("streak_bonus_cap", v)}
                compact
            />
        </div>

        <h4 className="text-sm font-medium text-slate-400 mb-3">Daily Login Reward Schedule</h4>
        <p className="text-slate-500 text-xs mb-3">Credits awarded for consecutive daily logins (repeating cycle).</p>
        <div className="space-y-2">
            <div className="grid grid-cols-[50px_1fr_1fr_40px] gap-3 text-slate-400 text-xs font-medium px-1">
                <div>Day</div>
                <div>Text Credits</div>
                <div>Voice Credits</div>
                <div></div>
            </div>
            {config.daily_login_rewards.schedule.map((day, idx) => (
                <div key={idx} className="grid grid-cols-[50px_1fr_1fr_40px] gap-3 items-center">
                    <div className="text-white text-sm font-medium pl-1">{idx + 1}</div>
                    <input
                        type="number"
                        min={0}
                        value={day.text_credits}
                        onChange={(e) => updateDailyLoginDay(idx, "text_credits", parseInt(e.target.value) || 0)}
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <input
                        type="number"
                        min={0}
                        value={day.voice_credits}
                        onChange={(e) => updateDailyLoginDay(idx, "voice_credits", parseInt(e.target.value) || 0)}
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <button
                        onClick={() => removeDailyLoginDay(idx)}
                        className="text-red-400 hover:text-red-300 text-sm transition-colors"
                        title="Remove day"
                    >
                        ✕
                    </button>
                </div>
            ))}
        </div>
        <button
            onClick={addDailyLoginDay}
            className="mt-3 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
        >
            + Add Day
        </button>
    </Section>
);
